import React from 'react';
import classNames from 'classnames';
import { type TFunction, type WithTranslation } from '@wix/yoshi-flow-editor';
import withTranslate from '../../hoc/with-translate';
import styles from './message.scss';

/**
 * `props` is whatever `showMessage(type, props)` was called with, so it varies
 * per message and each renderer narrows it itself.
 */
export type MessageContentFn = (t: TFunction, props?: unknown) => React.ReactNode;

export type MessageDescriptor = {
  content?: MessageContentFn | React.ReactElement;
  type?: string;
};

type Props = {
  alignment?: string;
  position?: string;
  content?: MessageDescriptor['content'];
  type?: string;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  top?: number;
  isInBackOffice?: boolean;
  message: MessageDescriptor;
  messageProps?: unknown;
} & Pick<WithTranslation, 't'>;

const Message = ({
  t,
  alignment,
  position,
  content,
  type,
  onClose,
  top = 0,
  isInBackOffice = false,
  message,
  messageProps,
}: Props) => {
  const resolvedType = type || message.type;
  const isDefaultStyle = !resolvedType;
  const containerClassName = classNames(
    styles.container,
    resolvedType && styles[resolvedType],
    alignment && styles[alignment],
    position && styles[position],
    {
      'blog-card-color': isDefaultStyle,
    },
    {
      [styles.backOffice]: isInBackOffice,
    },
    'message',
  );
  const style = {
    top: `${top}px`,
  };

  const resolvedContent = content || message.content;
  const renderContent: MessageContentFn = !resolvedContent
    ? () => null
    : typeof resolvedContent === 'function'
    ? resolvedContent
    : () => React.Children.only(resolvedContent);

  return (
    <div className={containerClassName} style={style}>
      {renderContent(t, messageProps)}
      {onClose && (
        <button className={classNames(styles.close, 'message__close')} onClick={onClose} />
      )}
    </div>
  );
};

export default withTranslate(Message);
